/* eslint-disable no-undef */
/* eslint-disable no-unused-vars */
/* eslint-disable react/prop-types */
import { Box, Button, Grid2, IconButton, Typography } from '@mui/material';
import React, { useEffect, useState, useMemo } from 'react';
import styles from './styles';
import { makeStyles } from '@mui/styles';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import VoltageIndicator from './VoltageIndicator'; // Asegúrate de que este componente exista
import { Link, useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { FiberManualRecord } from '@mui/icons-material';

// Registra los componentes necesarios de Chart.js para un gráfico de barras.
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const useStyles = makeStyles(styles);

export default function MimicoCard({ charge, groupId, typeEnergy }) {
  const classes = useStyles();
  // Estado para controlar qué vista se está mostrando (0: consumo, 1: demanda, 2: general)
  const [displayView, setDisplayView] = useState(0);
  // Estado para los datos que se muestran actualmente en la tarjeta
  const [dataDisplay, setDataDisplay] = useState({
    title: '',
    base: 0,
    intermedia: 0,
    punta: 0,
    total: 0,
    co2: 0, // Añadimos co2 para generalData
  });
  const intl = useIntl();
  const { state } = useLocation();
  const meter = state?.meter;
  const group = state?.group;

  /**
   * Formatea un número a una cadena con formato local (e.g., comas para miles, dos decimales).
   * @param {number | string} value - El valor numérico a formatear.
   * @returns {string} El valor formateado como cadena.
   */
  const formatNumberWithCommas = (value) => {
    const num = parseFloat(value);
    if (isNaN(num)) {
      return '0.00'; // O un valor por defecto si no es un número válido
    }
    return new Intl.NumberFormat('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const totalSum = (a, b, c) => {
    const numA = parseFloat(a) || 0;
    const numB = parseFloat(b) || 0;
    const numC = parseFloat(c) || 0;
    return formatNumberWithCommas(numA + numB + numC);
  };

  const findMaxDemand = (a, b, c) => {
    const numA = parseFloat(a) || 0;
    const numB = parseFloat(b) || 0;
    const numC = parseFloat(c) || 0;
    return formatNumberWithCommas(Math.max(numA, numB, numC));
  };

  // Objeto que contiene los datos de consumo
  const consumeData = useMemo(
    () => ({
      title: 'Consumo KWH',
      base: charge?.consumes?.[0]?.[1] || 0,
      intermedia: charge?.consumes?.[0]?.[2] || 0,
      punta: charge?.consumes?.[0]?.[3] || 0,
      total: totalSum(
        charge?.consumes?.[0]?.[1],
        charge?.consumes?.[0]?.[2],
        charge?.consumes?.[0]?.[3]
      ),
      co2: 0, // No aplica para consumo, lo mantenemos en 0 o undefined si no se usa
    }),
    [charge]
  );

  // Objeto que contiene los datos de demanda
  const demandData = useMemo(
    () => ({
      title: 'Demanda KW',
      base: charge?.consumes?.[1]?.[1] || 0,
      intermedia: charge?.consumes?.[1]?.[2] || 0,
      punta: charge?.consumes?.[1]?.[3] || 0,
      total: findMaxDemand(
        charge?.consumes?.[1]?.[1],
        charge?.consumes?.[1]?.[2],
        charge?.consumes?.[1]?.[3]
      ),
      co2: 0, // No aplica para demanda
    }),
    [charge]
  );

  // Objeto que contiene los datos generales (importe y CO2)
  const generalData = useMemo(
    () => ({
      title: 'Importe estimado $',
      total: charge?.total_average || '0.00', // Asegura 2 decimales para importe
      base: 0, // No aplica para general, lo mantenemos en 0
      intermedia: 0, // No aplica para general
      punta: 0, // No aplica para general
      co2: charge?.co2 || '0.00', // Valor de CO2
      voltage: charge?.voltage || '0.00', // Valor de voltage
    }),
    [charge]
  );

  // Manejar el clic para cambiar entre las 3 vistas
  useEffect(() => {
    if (displayView === 0) {
      setDataDisplay(consumeData);
    } else if (displayView === 1) {
      setDataDisplay(demandData);
    } else if (displayView === 2) {
      setDataDisplay(generalData);
    }
  }, [displayView]);

  // Cambiar displayView cada 10 segundos
  // y cuando le haga clic a un botón se resetee el conteo
  useEffect(() => {
    const intervalId = setInterval(() => {
      setDisplayView((prevView) => (prevView + 1) % 3);
    }, 8000);

    return () => {
      clearInterval(intervalId);
    };
  }, []);

  // Efecto que se ejecuta cuando el prop 'charge' cambia.
  // Inicializa la vista de la tarjeta con los datos de consumo.
  useEffect(() => {
    if (charge && charge.consumes) {
      setDataDisplay(consumeData);
      setDisplayView(0); // Asegura que empiece en la vista de consumo
    }
  }, [charge, consumeData]);

  // Configuración de opciones para el gráfico de barras de Chart.js.
  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'bottom',
          labels: {
            boxWidth: 0,
            font: {
              family: 'DS-DIGIT',
              size: 14,
            },
            color: 'rgba(0, 0, 0, 1)',
            padding: 15,
          },
        },
        title: {
          display: false,
          text: '',
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              if (context.parsed.y !== null) {
                label += new Intl.NumberFormat('es-MX', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(context.parsed.y);
              }
              return label;
            },
          },
          titleFont: {
            family: 'DS-DIGIT',
            size: 16,
            color: 'rgba(255, 255, 255, 1)',
          },
          bodyFont: {
            family: 'DS-DIGIT',
            size: 14,
            color: 'rgba(255, 255, 255, 1)',
          },
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
          borderColor: 'rgba(255, 255, 255, 0.5)',
          borderWidth: 1,
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            display: false,
            font: {
              family: 'DS-DIGIT',
              size: 12,
              color: 'rgba(255, 255, 255, 1)',
            },
          },
          stacked: false,
          categoryPercentage: 0.8,
          barPercentage: 0.9,
        },
        y: {
          beginAtZero: true,
          title: { display: false, text: '' },
          grid: { display: false },
          ticks: {
            display: false, // Puedes cambiar a true si quieres los valores en el eje Y
            font: { family: 'DS-DIGIT', size: 12 },
            color: 'rgba(255, 255, 255, 1)',
          },
        },
      },
      elements: {
        bar: {
          borderRadius: 8,
        },
      },
    }),
    [dataDisplay]
  );

  const labels = [''];

  const chartData = useMemo(
    () => ({
      labels,
      datasets: [
        {
          label: 'Base',
          data: [parseFloat(dataDisplay.base)],
          backgroundColor: 'rgba(100, 100, 100, 1)',
        },
        {
          label: 'Intermedia',
          data: [parseFloat(dataDisplay.intermedia)],
          backgroundColor: 'rgba(50, 50, 50, 1)',
        },
        {
          label: 'Punta',
          data: [parseFloat(dataDisplay.punta)],
          backgroundColor: 'rgba(0, 0, 0, 1)',
        },
      ],
    }),
    [dataDisplay, labels]
  );

  // Este componente ya está en tu código, solo lo incluí para contexto.
  // Asegúrate de que su implementación sea adecuada para mostrar los datos de 'generalData'.
  const displayVoltage = () => {
    return (
      <div
        style={{
          fontFamily: 'Lato, Helvetica Neue,Arial',
          textAlign: 'center',
        }}
      >
        {/* Aquí pasamos los datos de generalData a VoltageIndicator */}
        <VoltageIndicator
          charge={charge} // Mantiene el prop charge si VoltageIndicator lo necesita
          totalAverage={dataDisplay.total} // Pasa el total de generalData
          co2={dataDisplay.co2} // Pasa el co2 de generalData
        />
      </div>
    );
  };

  const displayButton = () => {
    if (charge.type_unit == 'VOLTAJE') {
      return (
        <Link
          to={`/group/${groupId}/charge/${charge.id}/alert`}
          state={{
            charge: charge,
          }}
        >
          <Button
            style={{
              width: '100%',
              backgroundColor: '#D64539',
              color: 'white',
            }}
            variant="contained"
          >
            {intl.formatMessage({ id: 'app.metersCards.card.voltageAlert' })}
          </Button>
        </Link>
      );
    } else {
      return (
        <Grid2 sx={12}>
          <Link
            state={{
              charge: charge,
              meter: meter,
              group: group,
            }}
            to={
              typeEnergy === 2
                ? `/dashboardSolarCharge/group/${groupId}/charge/${charge.id}`
                : `/chargeDashboard/group/${groupId}/charge/${charge.id}`
            }
          >
            <Button
              color="secondary"
              style={{
                width:
                  charge?.charge_status === 0
                    ? '100%'
                    : charge?.charge?.charge_pro === 0
                      ? '100%'
                      : '48%',
                backgroundColor: typeEnergy === 2 ? '#45B880' : '',
              }}
              variant="contained"
            >
              {/* {intl.formatMessage({ id: 'app.chargesCards.card.boardBtn' })}{' '} */}
              Perfil de Carga
              {typeEnergy === 2 ? 'Solar' : ''}
            </Button>
          </Link>
          {charge?.charge?.charge_pro !== 0 && charge?.charge_status === 1 && (
            <Link
              state={{
                charge: charge,
                meter: meter,
                group: group,
              }}
              to={`/dashboardChargePro/group/${groupId}/charge/${charge.id}`}
            >
              <Button
                color="secondary"
                style={{
                  width: '48%',
                  backgroundColor: typeEnergy === 2 ? '#45B880' : '',
                  marginLeft: '4%',
                }}
                variant="contained"
              >
                {/* {intl.formatMessage({ id: 'app.chargesCards.card.boardBtn' })}{' '}
                DE CALIDAD */}
                Calidad de Energía
              </Button>
            </Link>
          )}
        </Grid2>
      );
    }
  };

  console.log('charge', charge);

  return (
    <Box width="100%" height="100%">
      <Box className={classes.root}>
        <Box className={classes.title}>
          <Typography className={classes.titleText}>{charge?.name}</Typography>
          {charge.charge_status === 1 ? (
            <Box component="div" className={classes.ellipseGreen} />
          ) : (
            <Box component="div" className={classes.ellipseRed} />
          )}
        </Box>
        <Box className={classes.content}>
          <Box className={classes.display}>
            <Box className={classes.displayImage}>
              <Box
                className={classes.displayContent}
                gap={displayView !== 2 ? 1.5 : 7.4}
              >
                <Box display="flex" flexDirection="column" gap={1}>
                  <Typography
                    className={classes.displayTitle}
                    textAlign={'center'}
                  >
                    {charge.type_unit !== 'VOLTAJE'
                      ? dataDisplay.title
                      : 'Voltaje'}
                  </Typography>
                  <Box
                    display="flex"
                    flexDirection="row"
                    justifyContent="space-between"
                    alignItems="center"
                    my={2}
                  >
                    <Typography
                      className={classes.displaySubtitle}
                      variant="h2"
                    >
                      {charge.type_unit !== 'VOLTAJE'
                        ? 'Total'
                        : 'Voltaje Nominal'}
                    </Typography>
                    <Typography
                      className={classes.displaySubtitle2}
                      variant="h1"
                    >
                      {charge.type_unit !== 'VOLTAJE'
                        ? dataDisplay.total
                        : charge?.voltage || '0.00'}
                    </Typography>
                  </Box>
                  {charge.type_unit !== 'VOLTAJE' ? (
                    displayView !== 2 && charge?.rate_horary === 1 ? (
                      <>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            className={classes.displaySubtitle}
                            variant="h4"
                          >
                            Base
                          </Typography>
                          <Typography
                            className={classes.displaySubtitle}
                            variant="h2"
                          >
                            {formatNumberWithCommas(dataDisplay.base)}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            className={classes.displaySubtitle}
                            variant="h4"
                          >
                            Intermedia
                          </Typography>
                          <Typography
                            className={classes.displaySubtitle}
                            variant="h2"
                          >
                            {formatNumberWithCommas(dataDisplay.intermedia)}
                          </Typography>
                        </Box>
                        <Box
                          display="flex"
                          flexDirection="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography
                            className={classes.displaySubtitle}
                            variant="h4"
                          >
                            Punta
                          </Typography>
                          <Typography
                            className={classes.displaySubtitle}
                            variant="h2"
                          >
                            {formatNumberWithCommas(dataDisplay.punta)}
                          </Typography>
                        </Box>
                      </>
                    ) : (
                      displayView === 2 && (
                        <>
                          <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              className={classes.displaySubtitle}
                              variant="h4"
                            >
                              CO2
                            </Typography>
                            <Typography
                              className={classes.displaySubtitle}
                              variant="h2"
                            >
                              {dataDisplay.co2} KG
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            my={2}
                          >
                            <Typography
                              className={classes.displaySubtitle}
                              variant="h4"
                            >
                              Voltaje Nominal
                            </Typography>
                            <Typography
                              className={classes.displaySubtitle}
                              variant="h2"
                            >
                              {dataDisplay.voltage} V
                            </Typography>
                          </Box>
                        </>
                      )
                    )
                  ) : (
                    <Box
                      display="flex"
                      flexDirection="row"
                      justifyContent="space-between"
                      alignItems="center"
                    ></Box>
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          {charge.type_unit !== 'VOLTAJE' && (
            <Box className={classes.buttonsContent}>
              <Button
                className={
                  displayView === 0 ? classes.buttonActive : classes.button
                }
                onClick={() => setDisplayView(0)}
              >
                <Typography>KWH</Typography>
              </Button>
              <Button
                className={
                  displayView === 1 ? classes.buttonActive : classes.button
                }
                onClick={() => setDisplayView(1)}
              >
                <Typography>KW</Typography>
              </Button>
              <Button
                className={
                  displayView === 2 ? classes.buttonActive : classes.button
                }
                onClick={() => setDisplayView(2)}
              >
                <Typography>$</Typography>
              </Button>
            </Box>
          )}
        </Box>
        <Grid2 size={12} my={1}>
          {displayButton()}
        </Grid2>
      </Box>
    </Box>
  );
}
